import { useEffect, type RefObject } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'
import { useController, type Control, type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form'
import { CodeField, Cursor, useBlurOnFulfill, useClearByFocusCell } from 'react-native-confirmation-code-field'
import { useTranslation } from 'react-i18next'

import { type InputProps } from '@/components/base'

const CELL_COUNT = 6

interface TxInputProps<TValues extends FieldValues, TPath extends FieldPath<TValues>> extends InputProps {
  label?: string
  onForgotPassword?: () => void
  hint?: string
  control: Control<TValues>
  name: TPath
  rules?: RegisterOptions<TValues, TPath>
  hasPlaceholder?: boolean
  forceFocus?: boolean
}

export function TxInput<TValues extends FieldValues, TPath extends FieldPath<TValues>>({
  label,
  onForgotPassword,
  hint,
  control,
  name,
  rules,
  hasPlaceholder = true,
  forceFocus = false
}: TxInputProps<TValues, TPath>) {
  const { t } = useTranslation()
  const displayedLabel = label ?? t('auth:label.txPassword')
  const {
    field: { value, onChange },
    fieldState: { error }
  } = useController({ control, name, rules })
  const hasError = error !== undefined

  const ref = useBlurOnFulfill({ value, cellCount: CELL_COUNT }) as RefObject<TextInput>
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue: onChange
  })

  useEffect(() => {
    if (forceFocus && ref.current) {
      ref.current.focus()
    }
  }, [forceFocus])

  function handleChangeText(text: string) {
    const lastCharacter = text.slice(-1)
    if (lastCharacter !== '' && !/\d/.test(lastCharacter)) {
      return
    }
    onChange(text)
  }

  return (
    <View className={'gap-y-1 bg-white'}>
      <View className={'flex-row items-baseline justify-between'}>
        <Text className={'text-sm font-medium text-text-secondary'}>{displayedLabel}</Text>
        {onForgotPassword && (
          <Pressable onPress={onForgotPassword}>
            <Text className={'text-sm font-medium text-primary'}>{t('auth:forgetPassword')}</Text>
          </Pressable>
        )}
      </View>
      <Pressable accessible={false} onPress={() => ref.current?.focus()}>
        <CodeField
          ref={ref}
          {...props}
          value={value}
          onChangeText={handleChangeText}
          cellCount={CELL_COUNT}
          rootStyle={{ gap: 8 }}
          keyboardType={'number-pad'}
          textContentType={'oneTimeCode'}
          autoComplete={'sms-otp'}
          testID={`input-${String(name).replaceAll('.', '-')}`}
          accessibilityLabel={displayedLabel}
          renderCell={({ index, symbol, isFocused }) => {
            const shownSymbol = symbol ? '●' : ''
            return (
              <View
                key={index}
                className={'aspect-square flex-1 items-center justify-center rounded-md bg-fill-subtle'}
              >
                <Text className={'text-base text-text-primary'} onLayout={getCellOnLayoutHandler(index)}>
                  {shownSymbol || (isFocused ? <Cursor /> : null)}
                </Text>
              </View>
            )
          }}
        />
      </Pressable>
      {hint && <Text className={'text-xs font-medium text-text-tertiary'}>{hint}</Text>}
      {hasError && <Text className={'text-xs font-medium text-text-danger'}>{error.message}</Text>}
      {hasPlaceholder && !hasError && !hint && <View className={'h-4'} />}
    </View>
  )
}
